import * as React from 'react';
import type {
	FieldValues,
	UseControllerProps,
	ControllerRenderProps,
	UseFormUnregister,
	FieldPath,
} from 'react-hook-form';
import { useController } from 'react-hook-form';

import { isErrorType, type CellError } from 'af-components/RHFTableCellComponents/error.types';

export type RenderMethodParams<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TChangeValue = React.ChangeEvent> = {
		field: ControllerRenderProps<TFieldValues, TName>;
		/**
		 * Receives whatever the rendered component emits — a change event for native
		 * elements, a raw value for components that emit one.
		 */
		onFieldChange: (value: TChangeValue) => void;
		onFieldBlur: () => void;
		error?: Nullable<CellError>;
		label?: string;
	};

export type RHFFieldWrapperProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues,
	TChangeValue = React.ChangeEvent> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
		fieldsLength?: number;
		unregister: UseFormUnregister<TFieldValues>;
		onChange?: (name: string) => void;
		onBlur: () => void;
		render: (params: RenderMethodParams<TFieldValues, TName, TChangeValue>) => React.ReactElement;
		label?: string;
	};

/**
 * Field Wrapper around components
 * Offers controlled component by react hook form, tracks and updates RHF’s internal value store
 */
function RHFFieldWrapper<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues,
	TChangeValue = React.ChangeEvent>(props: RHFFieldWrapperProps<TFieldValues, TName, TTransformedValues, TChangeValue>) {
	const { name, control, defaultValue, rules, onChange, onBlur, render, label } = props;

	const { field, fieldState } = useController<TFieldValues, TName, TTransformedValues>({
		name,
		control,
		defaultValue,
		rules,
	});

	const onFieldChange = React.useCallback((value: TChangeValue) => {
		field.onChange(value);
		onChange?.(name);
	}, [field, name, onChange]);

	const onFieldBlur = React.useCallback(() => {
		field.onBlur();
		onBlur();
	}, [field, onBlur]);

	const error: Nullable<CellError> = React.useMemo(() => {
		if (fieldState.error?.message) {
			return {
				message: fieldState.error.message,
				type: isErrorType(fieldState.error.type)
					? fieldState.error.type
					: 'other',
			};
		} else {
			return null;
		}
	}, [fieldState]);

	return render({
		field,
		error,
		onFieldChange,
		onFieldBlur,
		label,
	});
}

export default React.memo(RHFFieldWrapper) as typeof RHFFieldWrapper;
